/**
 * SOLID Review
 * S — Strong. One concern: the real-time LiveKit client as the Framework drives it — connect, media
 *     control, the room's state and events — and the factory that produces one lazily.
 * O — Good. A new client capability is one more method; a new event payload one more union member.
 * L — N/A — interfaces only.
 * I — Acceptable. The client is wide by nature (13 methods spanning connect, media control and events),
 *     each read by useLiveKitSession or the video tiles.
 * D — Strong. The Application's own LiveKit vocabulary (`LiveKitConnectionState`), the event-type enum and
 *     the DOM's `MediaStreamTrack` — no `livekit-client` type.
 */
import type { RoomId, PlayerId } from '../../domain/uml-components/primitives';
import type { Result } from '../../shared/Result';

/**
 * THE LIVEKIT CLIENT, DECLARED BY THE LAYER THAT REQUIRES IT (frontend strict-ports plan, FE5d, 2026-10-04).
 * The family lived in `infrastructure/livekit/LiveKitClientAdapter.ts`, re-exported by an Infrastructure
 * barrel, because `IStateLiveKitRoom.connectionState` was the `livekit-client` SDK's `ConnectionState`
 * enum: moving it inward would have put a vendor type in the Application. The field is the Application's
 * `LiveKitConnectionState` now — the adapter maps the SDK's value (useLiveKitSession's `mapLkState`,
 * moved) — so the family moves inward and dependency-cruiser's last two Framework → Infrastructure
 * exemptions go (§28.78's last exception, closed).
 *
 * Still not a kernel port, and deliberately: the SDK's room, tracks and participants are main-thread,
 * non-JSON objects (§28.81 interior). The factory is a host resource handed to the Framework.
 */

/**
 * The room's events, as the client emits them. A runtime value (the hook's switch), so it lives in the
 * Application, which the Infrastructure adapter imports — never the other way (moved from the
 * `ComponentApplication` facade in FE6b, unchanged).
 */
export enum LiveKitRoomEventType {
  ParticipantConnected = 'PARTICIPANT_CONNECTED',
  ParticipantDisconnected = 'PARTICIPANT_DISCONNECTED',
  TrackSubscribed = 'TRACK_SUBSCRIBED',
  TrackUnsubscribed = 'TRACK_UNSUBSCRIBED',
  ConnectionStateChanged = 'CONNECTION_STATE_CHANGED',
  RoomDisconnected = 'ROOM_DISCONNECTED',
  LocalTrackPublished = 'LOCAL_TRACK_PUBLISHED',
  LocalTrackUnpublished = 'LOCAL_TRACK_UNPUBLISHED',
}

/**
 * A participant joining or leaving, as the client's ParticipantConnected/Disconnected events carry it.
 * Moved from `domain/uml-components/events.ts` in FE6b, when that module's never-emitted domain-event union
 * went (§29.4b) and this was its one live member.
 */
export interface IPayloadWebRTCParticipant {
  readonly participantId: string;
  readonly playerId: PlayerId;
  readonly trackSid: string | null;
}

/** The room's connection state, in this layer's own vocabulary — not the SDK's enum (FE5d). */
export type LiveKitConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'failed';

/** A participant as the video tiles render it — the hook's snapshot of the SDK's room. */
export interface IEntityLiveKitParticipant {
  readonly participantId: string;
  readonly playerId: PlayerId;
  readonly displayName: string;
  readonly isCameraEnabled: boolean;
  readonly isMicrophoneEnabled: boolean;
  readonly videoTrack: unknown | null;
  readonly audioTrack: unknown | null;
}

export interface IEntityLiveKitParticipantTrack {
  readonly participantIdentity: string;
  readonly participantSid: string;
  readonly isLocal: boolean;
  readonly videoTrack: MediaStreamTrack | null;
  readonly audioTrack: MediaStreamTrack | null;
  readonly isMicEnabled: boolean;
  readonly isCameraEnabled: boolean;
}

export interface IStateLiveKitRoom {
  readonly roomName: string;
  /** In the Application's vocabulary — the adapter maps the SDK's state (FE5d). */
  readonly connectionState: LiveKitConnectionState;
  readonly localParticipantIdentity: string | null;
  readonly participantTracks: ReadonlyArray<IEntityLiveKitParticipantTrack>;
}

/** What RoomDisconnected carries: whether the SERVER removed this participant (§21.10). */
export interface IPayloadLiveKitDisconnect {
  /** True when the backend's idle sweep removed us on purpose — the session must not rejoin by itself. */
  readonly removedByServer: boolean;
}

export interface IPayloadLiveKitRoom {
  readonly type: LiveKitRoomEventType;
  readonly payload: IPayloadWebRTCParticipant | IEntityLiveKitParticipantTrack | IStateLiveKitRoom | IPayloadLiveKitDisconnect | null;
}

export type LiveKitRoomEventHandler = (event: IPayloadLiveKitRoom) => void;

export interface IConfigLiveKitConnect {
  readonly roomId: RoomId;
  readonly playerId: PlayerId;
  readonly liveKitToken: string;
  readonly serverUrl: string;
  readonly enableVideo?: boolean;
  readonly enableAudio?: boolean;
}

export interface IGatewayLiveKit {
  connect(options: IConfigLiveKitConnect): Promise<Result<IStateLiveKitRoom>>;
  disconnect(roomId: RoomId): Promise<Result<void>>;
  getRoomState(roomId: RoomId): Result<IStateLiveKitRoom>;
  enableCamera(roomId: RoomId): Promise<Result<void>>;
  disableCamera(roomId: RoomId): Promise<Result<void>>;
  enableMicrophone(roomId: RoomId): Promise<Result<void>>;
  disableMicrophone(roomId: RoomId): Promise<Result<void>>;
  getLocalVideoTrack(roomId: RoomId): Result<MediaStreamTrack | null>;
  getRemoteVideoTrack(roomId: RoomId, participantIdentity: string): Result<MediaStreamTrack | null>;
  onRoomEvent(handler: LiveKitRoomEventHandler): () => void;
  offRoomEvent(handler: LiveKitRoomEventHandler): void;
  isConnected(roomId: RoomId): boolean;
  setParticipantVolume(roomId: RoomId, participantIdentity: string, volume: number): Result<void>;
}

/**
 * Produces a LiveKit client. Async because the real one lazy-imports the livekit-client SDK, a large chunk
 * only needed once the player opts into video.
 */
export interface IServiceLiveKitAdapter {
  create(): Promise<IGatewayLiveKit>;
}
